import { useEffect, useRef, useState } from 'react'

export const useDebouncedState = <T>(
  initialValue: T,
  delay: number = 500,
  debounceWhen?: (value: T) => boolean,
) => {
  const [loading, setLoading] = useState(false)
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue)

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear any pending timer on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const setValue = (value: T) => {
    // If we have an existing timer, cancel it (we're re-debouncing)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // If we shouldn't debounce this value, update immediately (not loading)
    if (debounceWhen && !debounceWhen(value)) {
      setLoading(false)
      setDebouncedValue(value)
      return
    }

    // We ARE debouncing
    setLoading(true)
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      setLoading(false)
      setDebouncedValue(value)
    }, delay)
  }

  return [debouncedValue, setValue, loading] as const
}
